/**
 * Benefits Summary Banner - Business Value Calculator
 * Sprint 7: Benefits Tab Redesign
 *
 * Sticky banner showing total benefits and driver statistics
 * Matches Costs tab summary banner UX
 */

import React from 'react';

interface BenefitsSummaryBannerProps {
  totalBenefits: number;
  totalRiskAdjusted: number;
  enabledDriverCount: number;
  totalDriverCount: number;
  threeYearProjection: number;
}

export const BenefitsSummaryBanner: React.FC<BenefitsSummaryBannerProps> = ({
  totalBenefits,
  totalRiskAdjusted,
  enabledDriverCount,
  totalDriverCount,
  threeYearProjection
}) => {
  const formatCurrency = (value: number): string => {
    if (value === 0) return '$0';

    // Format with K/M suffix for large numbers
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }

    return `$${value.toLocaleString()}`;
  };

  return (
    <div className="bv-summary-banner" role="region" aria-label="Benefits summary">
      <div className="bv-summary-grid">
        {/* Metric 1: Total Benefits (Base) */}
        <div className="bv-summary-metric">
          <div className="bv-metric-content">
            <div className="bv-metric-label">Total Benefits</div>
            <div className="bv-metric-value" title={`$${totalBenefits.toLocaleString()}`}>
              {formatCurrency(totalBenefits)}
            </div>
            <div className="bv-metric-sublabel">Base Value (Year 1)</div>
          </div>
        </div>

        {/* Metric 2: Enabled Drivers */}
        <div className="bv-summary-metric">
          <div className="bv-metric-content">
            <div className="bv-metric-label">Enabled Drivers</div>
            <div className="bv-metric-value">
              {enabledDriverCount} of {totalDriverCount}
            </div>
            <div className="bv-metric-sublabel">
              {totalDriverCount - enabledDriverCount} excluded
            </div>
          </div>
        </div>

        {/* Metric 3: Risk Adjusted */}
        <div className="bv-summary-metric">
          <div className="bv-metric-content">
            <div className="bv-metric-label">Risk Adjusted</div>
            <div className="bv-metric-value" title={`$${totalRiskAdjusted.toLocaleString()}`}>
              {formatCurrency(totalRiskAdjusted)}
            </div>
            <div className="bv-metric-sublabel">
              {totalRiskAdjusted > 0
                ? `${Math.round((totalRiskAdjusted / totalBenefits) * 100)}% of base`
                : 'No adjustments'}
            </div>
          </div>
        </div>

        {/* Metric 4: 3-Year Projection */}
        <div className="bv-summary-metric">
          <div className="bv-metric-content">
            <div className="bv-metric-label">3-Year Projection</div>
            <div className="bv-metric-value" title={`$${threeYearProjection.toLocaleString()}`}>
              {formatCurrency(threeYearProjection)}
            </div>
            <div className="bv-metric-sublabel">Year 1 + Year 2 + Year 3</div>
          </div>
        </div>
      </div>
    </div>
  );
};
